











































































import { computed, defineComponent, ref, toRef, watch } from "@vue/composition-api";
import { InputVaultFileSearch, VaultFile } from "@scrile/api-provider/dist/api/VaultsProvider";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { BaseMenuItem } from "@/types";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import BaseMenu from "@/components/BaseMenu";
import PageTitle from "@/components/PageTitle";
import VaultCategoryList from "../VaultController/components/VaultCategoryList";
import VaultFilesList from "../VaultController/components/VaultFilesList";
import VaultMenu from "../VaultController/components/VaultMenu";
import { useFilterVaultFiles } from "../VaultController/hooks/useFilterVaultFiles";
import { useVaultCategory } from "../VaultController/hooks/useVaultCategory";
import { useVaultFilesStatus } from "../VaultController/hooks/useVaultFilesStatus";
import { FilterName, FilterSubject, SortType } from "../VaultController/types";

export default defineComponent({
  name: "VaultSelectFiles",
  components: {
    BaseButton,
    BaseMenu,
    VaultFilesList,
    VaultCategoryList,
    VaultMenu,
    PageTitle,
    BaseIcon,
  },
  props: {
    maxFiles: { type: Number, default: Number.POSITIVE_INFINITY },
    filterSubject: { type: Object as () => FilterSubject },
  },
  setup(props, { emit }) {
    const filterItems: BaseMenuItem[] = [
      { name: FilterName.ALL, text: $t("All files") },
      { name: FilterName.UNUSED, text: $t("Unused") },
    ];

    const { authUserStrict } = useAuthUser();
    const { fileUsageSubject } = useFilterVaultFiles(toRef(props, "filterSubject"));
    const { usedFilesMap, updateUsedFilesMap } = useVaultFilesStatus(fileUsageSubject);
    const { vaultCategories, categoryScroll, getFirstCategoryPage, getNextCategoryPage } = useVaultCategory();

    const selectedCategoryId = ref("");
    const sortType = ref(SortType.LAST_USE_TIME_DESC);
    const activeFilter = ref(filterItems[0]);
    const checkedFiles = ref<VaultFile[]>([]);
    let clearFiles = () => {};

    const selectedCategory = computed(() => vaultCategories.value.find(i => i.id === selectedCategoryId.value) ?? null);
    const showUnusedFilesOnly = computed(() => activeFilter.value.name === FilterName.UNUSED);

    const {
      enableScroll: fileScroll,
      items: vaultFiles,
      getNextPage: getNextFilePage,
      getFirstPage: getFiles,
    } = usePager<VaultFile, InputVaultFileSearch>(
      (page, data) => {
        return providers.VaultsProvider.search({
          data: {
            userId: authUserStrict.value.id,
            categoryIds: selectedCategoryId.value ? [selectedCategoryId.value] : undefined,
            page,
            sort: sortType.value,
            unusedIn: showUnusedFilesOnly.value ? fileUsageSubject.value : undefined,
            ...data,
          },
        });
      },
      (files: VaultFile[]) => {
        updateUsedFilesMap(files, authUserStrict.value.id);
      }
    );

    const onUnusedFilterClick = async (name: string) => {
      activeFilter.value = filterItems.find(x => x.name === name) ?? filterItems[0];
    };

    const handleSort = async (newSortType: SortType) => {
      sortType.value = newSortType;
    };

    const onBack = () => {
      selectedCategoryId.value = "";
      sortType.value = SortType.LAST_USE_TIME_DESC;
    };

    watch(
      selectedCategoryId,
      async currentValue => {
        try {
          emit("loading", true);
          const requests = [getFiles()];
          if (!currentValue && !vaultCategories.value.length) {
            requests.push(getFirstCategoryPage());
          }
          await Promise.all(requests);
        } finally {
          emit("loading", false);
        }
      },
      { immediate: true }
    );

    watch([activeFilter, sortType], async () => {
      await getFiles();
    });

    return {
      checkedFiles,
      fileScroll,
      categoryScroll,
      selectedCategoryId,
      selectedCategory,
      filterItems,
      activeFilter,
      vaultCategories,
      vaultFiles,
      sortType,
      usedFilesMap,
      getNextFilePage,
      getNextCategoryPage,
      clearFiles,
      onBack,
      handleSort,
      onUnusedFilterClick,
    };
  },
});
